import React from 'react';
import { Users, Clock, CheckCircle, TrendingUp } from 'lucide-react';

const SummaryTab = ({ survey }) => {
  const responses = survey.responses || [];
  const totalResponses = responses.length;
  
  const avgCompletionTime = responses.length > 0
    ? Math.round(responses.reduce((sum, r) => sum + (r.completionTime || 0), 0) / responses.length)
    : 0;

  const formatTime = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const getResponseRate = () => {
    const completedResponses = responses.filter(r => {
      const requiredQuestions = survey.questions.filter(q => q.required);
      return requiredQuestions.every(q => r.answers && r.answers[q.id] !== undefined);
    }).length;
    return totalResponses > 0 ? Math.round((completedResponses / totalResponses) * 100) : 0;
  };

  const getDateRange = () => {
    if (responses.length === 0) return 'No responses yet';
    const dates = responses.map(r => new Date(r.submittedAt)).sort((a, b) => a - b);
    const first = dates[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const last = dates[dates.length - 1].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return dates.length === 1 ? first : `${first} - ${last}`;
  };

  return (
    <div className="summary-tab">
      <div className="summary-grid">
        <div className="summary-card">
          <div className="summary-card-icon">
            <Users size={24} />
          </div>
          <div className="summary-card-value">{totalResponses}</div>
          <div className="summary-card-label">Total Responses</div>
        </div>

        <div className="summary-card">
          <div className="summary-card-icon">
            <CheckCircle size={24} />
          </div>
          <div className="summary-card-value">{getResponseRate()}%</div>
          <div className="summary-card-label">Completion Rate</div>
        </div>

        <div className="summary-card">
          <div className="summary-card-icon">
            <Clock size={24} />
          </div>
          <div className="summary-card-value">{formatTime(avgCompletionTime)}</div>
          <div className="summary-card-label">Avg. Completion Time</div>
        </div>

        <div className="summary-card">
          <div className="summary-card-icon">
            <TrendingUp size={24} />
          </div>
          <div className="summary-card-value">{survey.questions?.length || 0}</div>
          <div className="summary-card-label">Questions</div>
        </div>
      </div>

      <div className="summary-details">
        <div className="detail-row">
          <span className="detail-label">Collection Period</span>
          <span className="detail-value">{getDateRange()}</span>
        </div>
        <div className="detail-row">
          <span className="detail-label">Survey Status</span>
          <span className={`status-badge ${survey.status}`}>{survey.status}</span>
        </div>
        <div className="detail-row">
          <span className="detail-label">Created</span>
          <span className="detail-value">
            {new Date(survey.createdAt).toLocaleDateString('en-US', { 
              month: 'long', day: 'numeric', year: 'numeric' 
            })}
          </span>
        </div>
      </div>

      <style>{`
        .summary-tab {
          padding: 24px;
        }
        .summary-card-icon {
          width: 48px;
          height: 48px;
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          margin: 0 auto 12px;
        }
        .summary-details {
          background: #f8fafc;
          border-radius: 12px;
          padding: 20px;
          margin-top: 24px;
        }
        .detail-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 0;
          border-bottom: 1px solid #e2e8f0;
        }
        .detail-row:last-child {
          border-bottom: none;
        }
        .detail-label {
          font-size: 14px;
          color: #64748b;
        }
        .detail-value {
          font-size: 14px;
          font-weight: 500;
          color: #1e293b;
        }
        .status-badge {
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 13px;
          font-weight: 500;
          text-transform: capitalize;
        }
        .status-badge.draft {
          background: #fef3c7;
          color: #92400e;
        }
        .status-badge.live {
          background: #dcfce7;
          color: #166534;
        }
        .status-badge.closed {
          background: #fee2e2;
          color: #991b1b;
        }
      `}</style>
    </div>
  );
};

export default SummaryTab;
